import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PersonIcon from '@mui/icons-material/Person'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import {
  Box,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { ReactNode, useState } from 'react'
import { toast } from 'react-toastify'

import CopyToClipboardButton from '~/components/copyToClipboardButton'
import Timestamp from '~/features/inbox/components/chatSection/timestamp'
import { ESpeaker } from '~/features/inbox/models'
import rem from '~/utils/ui'

type TProps = {
  name: string | ReactNode
  activityDate: string
  children: ReactNode
  speaker: ESpeaker
  icon?: ReactNode
  detail?: string
  link?: string
  extraInfo?: ReactNode
  copyUrl?: string
  copyText?: string
}

const MENU_ITEM_STYLES: SxProps<Theme> = {
  borderRadius: rem(8),
  mx: rem(2),
}

function MessageActionsMenu({
  copyUrl,
  copyText,
  color,
  openTowardsLeft,
}: {
  copyUrl: string
  copyText: string
  color: string
  openTowardsLeft: boolean
}) {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [copied, setCopied] = useState(false)
  const open = Boolean(anchorEl)
  // Flyout beside the icon at the same height, not stacked below it: the
  // anchor point sits on the icon's near-to-center edge, and the menu's
  // opposite edge is pinned there so it opens outward from that edge.
  const anchorHorizontal = openTowardsLeft ? 'left' : 'right'
  const transformHorizontal = openTowardsLeft ? 'right' : 'left'

  const handleClose = () => setAnchorEl(null)

  // Matches CopyToClipboardButton's "Copied!" tooltip elsewhere in the app —
  // but that one relies on the cursor still hovering the button after click
  // (same element). Here the click lands on a menu item that then unmounts,
  // so the tooltip on the trigger icon has to be force-opened via state
  // instead of relying on hover.
  const showCopied = () => {
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCopyLink = () => {
    if (copyUrl) {
      navigator.clipboard
        .writeText(copyUrl)
        .then(showCopied)
        .catch(() => toast.error('Failed to copy link'))
    }
    handleClose()
  }

  const handleCopyText = () => {
    if (copyText) {
      navigator.clipboard
        .writeText(copyText)
        .then(showCopied)
        .catch(() => toast.error('Failed to copy message'))
    }
    handleClose()
  }

  return (
    <>
      <Tooltip title="Copied!" open={copied} placement="right-start">
        <IconButton
          size="small"
          aria-label="Message actions"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ p: rem(2), color }}
        >
          <MoreVertIcon sx={{ fontSize: rem(20) }} />
        </IconButton>
      </Tooltip>
      <Menu
        // MUI's default variant ("selectedMenu") re-anchors to whichever item
        // it considers "selected" and ignores our anchor/transform origins,
        // which made the menu render on top of the icon instead of beside it.
        // "menu" makes it respect anchorOrigin/transformOrigin literally.
        variant="menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: anchorHorizontal }}
        transformOrigin={{
          vertical: 'center',
          horizontal: transformHorizontal,
        }}
        sx={{ '.MuiList-root': { padding: rem(6) } }}
        slotProps={{
          paper: {
            sx: {
              // The app-wide MuiCssBaseline reset (`body .MuiPaper-root { borderRadius: 0 }`)
              // outranks a plain `.MuiPaper-root` class on specificity alone, so it wins over
              // this override unless forced — hence `!important` on both here.
              borderRadius: `${rem(12)} !important`,
              boxShadow: `${theme.shadows[2]} !important`,
              [openTowardsLeft ? 'mr' : 'ml']: rem(6),
            },
          },
        }}
      >
        {copyUrl && (
          <MenuItem onClick={handleCopyLink} sx={MENU_ITEM_STYLES}>
            <ListItemIcon>
              <LinkOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Copy link to message" />
          </MenuItem>
        )}
        {copyText && (
          <MenuItem onClick={handleCopyText} sx={MENU_ITEM_STYLES}>
            <ListItemIcon>
              <ContentCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Copy message text" />
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

function BaseChatBubble({
  name,
  activityDate,
  children,
  speaker,
  icon = null,
  detail = '',
  link = '',
  extraInfo = null,
  copyUrl = '',
  copyText = '',
}: TProps) {
  const theme = useTheme()

  const isUserMessage = speaker === ESpeaker.user
  const isAssistantMessage = speaker === ESpeaker.assistant

  // AI messages: dark navy background with white text
  // User messages: white background with dark text
  const assistantBubbleBackgroundColor = theme.palette.menuBackground
  const assistantBubbleFontColor = theme.palette.menuFont
  const userBubbleBackgroundColor = theme.palette.common.white
  const userBubbleFontColor = theme.palette.mainFont

  // Determine which icon to show based on speaker
  // Use provided icon if available, otherwise use speaker-based icon
  const speakerIcon =
    icon ||
    (isAssistantMessage ? (
      <SmartToyOutlinedIcon
        sx={{ width: rem(20), height: rem(20), color: theme.palette.tertiary4 }}
      />
    ) : (
      <PersonIcon
        sx={{ width: rem(20), height: rem(20), color: theme.palette.tertiary4 }}
      />
    ))

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '80%',
        marginY: rem(8),
        alignSelf: isAssistantMessage ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          padding: rem(12),
          paddingBottom: rem(4),
          borderRadius: rem(20),
          marginBottom: rem(8),
          borderBottomRightRadius: isAssistantMessage ? rem(0) : rem(20),
          borderBottomLeftRadius: isUserMessage ? rem(0) : rem(20),
          backgroundColor: isAssistantMessage
            ? assistantBubbleBackgroundColor
            : userBubbleBackgroundColor,
          color: isAssistantMessage
            ? assistantBubbleFontColor
            : userBubbleFontColor,
          wordBreak: 'break-word',
          a: {
            color: isAssistantMessage
              ? assistantBubbleFontColor
              : userBubbleFontColor,
          },
          'a:hover': {
            color: isAssistantMessage
              ? assistantBubbleFontColor
              : userBubbleFontColor,
          },
        }}
      >
        {children}
        <Stack direction="row-reverse">
          <Timestamp activityDate={activityDate} sx={{ position: 'unset' }} />
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: rem(8),
          fontSize: rem(12),
          color: theme.palette.tertiary4,
          flexDirection: isAssistantMessage ? 'row-reverse' : 'row',
          flexWrap: 'wrap',
        }}
      >
        <Tooltip title={detail} placement="bottom">
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: rem(8),
              flexWrap: 'wrap',
            }}
          >
            {link ? (
              <Link
                href={link}
                variant="body2"
                sx={{ textDecoration: 'none', color: theme.palette.tertiary4 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(4),
                  }}
                >
                  {speakerIcon}
                  {typeof name === 'string' ? (
                    <Typography variant="body2">{name}</Typography>
                  ) : (
                    name
                  )}
                </Box>
              </Link>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(4),
                  }}
                >
                  {speakerIcon}
                  {typeof name === 'string' ? (
                    <Typography variant="body2">{name}</Typography>
                  ) : (
                    name
                  )}
                </Box>
                {extraInfo && (
                  <>
                    <Box
                      sx={{
                        width: rem(4),
                        height: rem(4),
                        borderRadius: '50%',
                        backgroundColor: theme.palette.tertiary4,
                        opacity: 0.6,
                      }}
                    />
                    {extraInfo}
                  </>
                )}
              </>
            )}
          </Box>
        </Tooltip>
        {copyText
          ? (copyUrl || copyText) && (
              <MessageActionsMenu
                copyUrl={copyUrl}
                copyText={copyText}
                color={theme.palette.tertiary4}
                openTowardsLeft={isAssistantMessage}
              />
            )
          : copyUrl && (
              <CopyToClipboardButton
                textToCopy={copyUrl}
                icon={LinkOutlinedIcon}
                iconButtonProps={{
                  size: 'small',
                  'aria-label': 'Copy link to this message',
                  sx: { p: rem(2), color: theme.palette.tertiary4 },
                }}
                iconSxProps={{ fontSize: rem(20) }}
              />
            )}
      </Box>
    </Box>
  )
}

export default BaseChatBubble
